import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { CheckIcon, FileTextIcon, PenToolIcon, SearchIcon, TargetIcon, TrendingUpIcon, UsersIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
export function ContentService() {
  const features = [{
    icon: PenToolIcon,
    title: 'Website Copywriting',
    description: 'Engaging, conversion-focused content for all your pages'
  }, {
    icon: FileTextIcon,
    title: 'Blog Posts',
    description: 'SEO-optimized articles that attract and engage readers'
  }, {
    icon: SearchIcon,
    title: 'SEO Content',
    description: 'Keyword-rich content that ranks well in search engines'
  }, {
    icon: UsersIcon,
    title: 'Social Media',
    description: 'Compelling posts that drive engagement and shares'
  }, {
    icon: TargetIcon,
    title: 'Product Descriptions',
    description: 'Persuasive copy that converts browsers into buyers'
  }, {
    icon: TrendingUpIcon,
    title: 'Email Campaigns',
    description: 'Newsletter and promotional content that drives action'
  }];
  const benefits = ['Save time by outsourcing content creation', 'Improve SEO rankings with quality content', 'Engage your audience with professional copy', 'Increase conversions with persuasive messaging', 'Maintain consistent brand voice across platforms', 'Focus on your business while we handle the writing'];
  const process = [{
    step: '01',
    title: 'Discovery Call',
    description: 'We learn about your business, target audience, brand voice, and content goals.'
  }, {
    step: '02',
    title: 'Research & Planning',
    description: 'We research your industry, competitors, and keywords to create a content strategy.'
  }, {
    step: '03',
    title: 'Content Creation',
    description: 'Our writers create high-quality, engaging content tailored to your needs and audience.'
  }, {
    step: '04',
    title: 'Review & Revisions',
    description: 'You review the content and request any changes. We refine until you are completely satisfied.'
  }, {
    step: '05',
    title: 'Delivery & Publishing',
    description: 'We deliver the final content and can help with publishing to your website or platforms.'
  }];
  const packages = [{
    name: 'Starter Content',
    price: '£250',
    features: ['Up to 5 pages of website copy', 'SEO keyword research', 'Basic brand voice guide', '2 rounds of revisions', '5-7 day turnaround']
  }, {
    name: 'Growth Content',
    price: '£500',
    features: ['Up to 10 pages of website copy', 'Advanced SEO optimisation', '4 blog posts (500-800 words)', 'Detailed brand voice guide', '3 rounds of revisions', '7-10 day turnaround'],
    popular: true
  }, {
    name: 'Enterprise Content',
    price: '£1,000+',
    features: ['Unlimited pages of website copy', 'Comprehensive content strategy', '8+ blog posts per month', 'Social media content', 'Email campaigns', 'Unlimited revisions', 'Priority turnaround']
  }];
  return <>
      <Helmet data-id="element-1924">
        <title data-id="element-1925">
          Content Writing Services UK | Website Copy & Blog Posts – Bright
          Pixxel
        </title>
        <meta name="description" content="Professional content writing services for websites, blogs, and marketing. SEO-optimized copy that converts from £250." data-id="element-1926" />
      </Helmet>
      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-12 bg-gradient-to-b from-[#0C1B26] to-[#212C34]" data-id="element-1927">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1928">
          <div className="max-w-4xl mx-auto text-center" data-id="element-1929">
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.5
          }} className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#0DC3DC]/20 flex items-center justify-center" data-id="element-1930">
              <PenToolIcon size={40} className="text-[#0DC3DC]" data-id="element-1931" />
            </motion.div>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-3xl md:text-5xl font-bold mb-6 text-white" data-id="element-1932">
              Content Writing Services
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="text-lg md:text-xl text-white/90 mb-8" data-id="element-1933">
              Professional, engaging content that connects with your audience
              and drives results. From website copy to blog posts, we create
              content that works.
            </motion.p>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1934">
              <Link to="/contact" className="bg-[#0DC3DC] hover:bg-[#0DC3DC]/90 text-white px-8 py-3 rounded-lg font-medium transition-colors shadow-lg" data-id="element-1935">
                Get a Quote
              </Link>
              <Link to="/portfolio" className="bg-white/10 backdrop-blur-sm border-2 border-white text-white hover:bg-white hover:text-[#0C1B26] px-8 py-3 rounded-lg font-medium transition-colors" data-id="element-1936">
                View Samples
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
      {/* Features Grid */}
      <section className="py-16 bg-white dark:bg-[#1E1E1E]" data-id="element-1937">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1938">
          <div className="max-w-6xl mx-auto" data-id="element-1939">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-center mb-12" data-id="element-1940">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-1941">
                Content Writing Services
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300" data-id="element-1942">
                Professional content for every aspect of your business
              </p>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1943">
              {features.map((feature, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="bg-[#F9FAFB] dark:bg-[#212C34] p-6 rounded-xl hover:shadow-lg transition-shadow" data-id="element-1944">
                  <div className="w-12 h-12 bg-[#0DC3DC]/20 rounded-lg flex items-center justify-center mb-4" data-id="element-1945">
                    <feature.icon size={24} className="text-[#0DC3DC]" data-id="element-1946" />
                  </div>
                  <h3 className="text-xl font-bold mb-2" data-id="element-1947">{feature.title}</h3>
                  <p className="text-gray-600 dark:text-gray-300" data-id="element-1948">
                    {feature.description}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>
      {/* Benefits Section with Image */}
      <section className="py-16 bg-[#F9FAFB] dark:bg-[#121212]" data-id="element-1949">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1950">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-1951">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-1952">
              <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-1953">
                Why Professional Content Matters
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 mb-8" data-id="element-1954">
                Quality content is the foundation of successful digital
                marketing. It helps you connect with your audience, build trust,
                and drive conversions.
              </p>
              <div className="space-y-4" data-id="element-1955">
                {benefits.map((benefit, index) => <motion.div key={index} initial={{
                opacity: 0,
                x: -20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.5,
                delay: index * 0.1
              }} className="flex items-center gap-3" data-id="element-1956">
                    <div className="w-6 h-6 rounded-full bg-[#0DC3DC]/20 flex items-center justify-center flex-shrink-0" data-id="element-1957">
                      <CheckIcon size={14} className="text-[#0DC3DC]" data-id="element-1958" />
                    </div>
                    <span className="text-gray-700 dark:text-gray-300" data-id="element-1959">
                      {benefit}
                    </span>
                  </motion.div>)}
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="rounded-xl overflow-hidden shadow-xl" data-id="element-1960">
              <img src="https://images.unsplash.com/photo-1455390582262-044cdead277a?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" alt="Professional content writer working on engaging blog posts and website copy - Bright Pixxel Studios content writing services" className="w-full h-auto" loading="lazy" decoding="async" data-id="element-1961" />
            </motion.div>
          </div>
        </div>
      </section>
      {/* Process Section */}
      <section className="py-16 bg-white dark:bg-[#1E1E1E]" data-id="element-1962">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1963">
          <div className="max-w-4xl mx-auto" data-id="element-1964">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-3xl md:text-4xl font-bold mb-12 text-center" data-id="element-1965">
              Our Content Creation Process
            </motion.h2>
            <div className="space-y-8" data-id="element-1966">
              {process.map((item, index) => <motion.div key={index} initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="flex gap-6" data-id="element-1967">
                  <div className="flex-shrink-0" data-id="element-1968">
                    <div className="w-16 h-16 rounded-full bg-[#0DC3DC]/20 flex items-center justify-center" data-id="element-1969">
                      <span className="text-2xl font-bold text-[#0DC3DC]" data-id="element-1970">
                        {item.step}
                      </span>
                    </div>
                  </div>
                  <div className="flex-grow" data-id="element-1971">
                    <h3 className="text-xl font-bold mb-2" data-id="element-1972">{item.title}</h3>
                    <p className="text-gray-600 dark:text-gray-300" data-id="element-1973">
                      {item.description}
                    </p>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>
      {/* Packages Section */}
      <section className="py-16 bg-[#F9FAFB] dark:bg-[#121212]" data-id="element-1974">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1975">
          <div className="max-w-6xl mx-auto" data-id="element-1976">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-3xl md:text-4xl font-bold mb-12 text-center" data-id="element-1977">
              Content Writing Packages
            </motion.h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1978">
              {packages.map((pkg, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className={`bg-white dark:bg-[#1E1E1E] p-8 rounded-xl shadow-md ${pkg.popular ? 'ring-2 ring-[#0DC3DC]' : ''}`} data-id="element-1979">
                  {pkg.popular && <div className="text-center mb-4" data-id="element-1980">
                      <span className="bg-[#0DC3DC] text-white px-4 py-1 rounded-full text-sm font-medium" data-id="element-1981">
                        Most Popular
                      </span>
                    </div>}
                  <h3 className="text-2xl font-bold mb-2" data-id="element-1982">{pkg.name}</h3>
                  <div className="text-3xl font-bold text-[#0DC3DC] mb-6" data-id="element-1983">
                    {pkg.price}
                  </div>
                  <ul className="space-y-3" data-id="element-1984">
                    {pkg.features.map((feature, fIndex) => <li key={fIndex} className="flex items-start gap-3" data-id="element-1985">
                        <CheckIcon size={18} className="text-[#0DC3DC] mt-0.5 flex-shrink-0" data-id="element-1986" />
                        <span className="text-gray-700 dark:text-gray-300" data-id="element-1987">
                          {feature}
                        </span>
                      </li>)}
                  </ul>
                  <Link to="/contact" className={`mt-8 block text-center px-6 py-3 rounded-lg font-medium transition-colors ${pkg.popular ? 'bg-[#0DC3DC] text-white hover:bg-[#0DC3DC]/90' : 'bg-white dark:bg-[#1E1E1E] border-2 border-[#0DC3DC] text-[#0DC3DC] hover:bg-[#0DC3DC] hover:text-white'}`} data-id="element-1988">
                    Get Started
                  </Link>
                </motion.div>)}
            </div>
            <div className="mt-8 text-center" data-id="element-1989">
              <p className="text-gray-600 dark:text-gray-300" data-id="element-1990">
                Need a custom package? We can create a tailored solution for
                your specific needs.
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* Stats Section */}
      <section className="py-16 bg-white dark:bg-[#1E1E1E]" data-id="element-1991">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1992">
          <div className="max-w-5xl mx-auto" data-id="element-1993">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-3xl md:text-4xl font-bold mb-12 text-center" data-id="element-1994">
              Content That Delivers Results
            </motion.h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1995">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.1
            }} className="text-center" data-id="element-1996">
                <div className="text-5xl font-bold text-[#0DC3DC] mb-2" data-id="element-1997">
                  +150%
                </div>
                <p className="text-gray-600 dark:text-gray-300" data-id="element-1998">
                  Average increase in organic traffic
                </p>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.2
            }} className="text-center" data-id="element-1999">
                <div className="text-5xl font-bold text-[#0DC3DC] mb-2" data-id="element-2000">
                  +85%
                </div>
                <p className="text-gray-600 dark:text-gray-300" data-id="element-2001">
                  Improvement in engagement rates
                </p>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.3
            }} className="text-center" data-id="element-2002">
                <div className="text-5xl font-bold text-[#0DC3DC] mb-2" data-id="element-2003">
                  +120%
                </div>
                <p className="text-gray-600 dark:text-gray-300" data-id="element-2004">
                  Increase in conversion rates
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-b from-[#0C1B26] to-[#212C34]" data-id="element-2005">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2006">
          <div className="max-w-3xl mx-auto text-center" data-id="element-2007">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-3xl md:text-4xl font-bold mb-6 text-white" data-id="element-2008">
              Ready to Elevate Your Content?
            </motion.h2>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-lg text-white/90 mb-8" data-id="element-2009">
              Get in touch today to discuss your content needs. We'll create
              engaging, SEO-optimised content that resonates with your audience
              and drives results.
            </motion.p>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} data-id="element-2010">
              <a href="mailto:info@bright-pixxel.com?subject=New%20Enquiry%20-%20Content%20Writing" className="inline-block bg-[#0DC3DC] hover:bg-[#0DC3DC]/90 text-white px-8 py-4 rounded-lg font-bold transition-colors shadow-lg text-lg" data-id="element-2011">
                Request a Quote
              </a>
            </motion.div>
          </div>
        </div>
      </section>
    </>;
}